import { useMemo } from "react";

import useUserRoles from "hooks/use-user-roles.hook";
import type { OpportunityAppointment } from "services/types/sales-coach.type";

import { canViewSalesCoachReview } from "../utils/sales-coach.access";
import useSalesCoachVersion, {
  useSalesCoachVersionProbe
} from "./sales-coach-version.hook";
import { useSalesCoachOpportunitiesQuery } from "./use-sales-coach-api.hook";
import useSalesCoachRouteAccess from "./use-sales-coach-route-access.hook";

export interface UseSalesCoachReviewAccessArgs {
  /** Resolve by appointment. Exactly one of this and `scribeId`. */
  appointmentId?: number;
  /** Resolve by recording, for surfaces that have no appointment id. */
  scribeId?: number;
  /**
   * The booked clinician, when the caller already knows it.
   *
   * Supplying it lets the role rule run BEFORE the request, so a surface whose user cannot see
   * the review never asks for it. The appointment page has this from `appointment.staffMemberId`;
   * the scribe modal does not (`AmbientScribe.staff` is who recorded, which is not always who
   * the visit was booked to — pre-filtering on it would deny a provider their own review).
   */
  knownProviderId?: number | null;
  /** The direct supervising provider, where the caller knows it. */
  supervisingProviderId?: number | null;
  skip?: boolean;
}

export interface SalesCoachReviewAccess {
  /** Render nothing: the answer is not knowable yet. */
  isResolving: boolean;
  /** Only meaningful once `isResolving` is false. */
  canView: boolean;
  /** The resolved row, or `null` when there isn't one for this key. */
  row: OpportunityAppointment | null;
  /** The API answered without applying our filter — see the `count` note below. */
  isBackendUnsupported: boolean;
  isError: boolean;
}

const EMPTY: OpportunityAppointment[] = [];

/**
 * Whether this user may see the Sales Coach review for one appointment or one recording, and
 * the row it resolves to.
 *
 * Shared by the appointment-page card and the scribe modal's tab so the two can never disagree
 * about who owns an appointment.
 *
 * **`count` is the backend-presence probe, and it is the load-bearing guard.** The `appointment`
 * and `scribe` filters are a backend addition (SN-19586), and django-filter *silently ignores* an
 * unknown query param — so against an API that predates them the filter is dropped and the
 * response is an UNFILTERED first page. With the filter applied, `count` can only be 0 or 1;
 * without it, `count` is the practice's entire eligible set. Matching on `results` alone is NOT
 * enough: `limit: 1` returns the newest appointment, which is very often the one the clinician is
 * looking at, so an id check would pass by coincidence exactly when it matters most.
 *
 * ⚠️ Discarding is not the same as not fetching. With the flag on before the backend ships, the
 * request is still made and its body still contains another patient's row. **Deploy the backend
 * first** — see `docs/ambient-scribes/sales-coach/SN-19586-BACKEND-REQUIREMENTS.md`.
 */
export default function useSalesCoachReviewAccess({
  appointmentId,
  scribeId,
  knownProviderId,
  supervisingProviderId,
  skip = false
}: UseSalesCoachReviewAccessArgs): SalesCoachReviewAccess {
  const { isResolving: isFlagResolving, isEnabled } = useSalesCoachRouteAccess();
  const { isAdminOrBusinessOwner, userId } = useUserRoles();

  /*
   * The pre-request role check. Only possible where the caller knows the booked clinician; it
   * can only ever DENY, because the row's own `provider_id` is re-checked below either way.
   */
  const isDeniedLocally =
    knownProviderId !== undefined &&
    !canViewSalesCoachReview({
      isAdminOrBusinessOwner,
      userId,
      providerId: knownProviderId,
      supervisingProviderId
    });

  const hasKey = appointmentId != null || scribeId != null;
  const isQuerySkipped = skip || !isEnabled || !hasKey || isDeniedLocally;

  /*
   * The second of the two roots that settle the version (the section layout is the other).
   * Skipped wherever the row is, so a page that shows no card never downloads the catalog.
   * The card and the tab body read the same latch, so they always agree with this row.
   */
  useSalesCoachVersionProbe({ skip: isQuerySkipped });
  const version = useSalesCoachVersion();
  const isVersionResolving = !isQuerySkipped && version === undefined;

  const { currentData, isError } = useSalesCoachOpportunitiesQuery(
    appointmentId != null
      ? { appointment: [appointmentId], limit: 1 }
      : { scribe: [scribeId as number], limit: 1 },
    { skip: isQuerySkipped }
  );

  const isBackendUnsupported = (currentData?.count ?? 0) > 1;

  /*
   * `currentData`, never `data`: RTK deliberately retains the PREVIOUS arg's response while a
   * new one is in flight, which here would mean one appointment's review under another's header.
   */
  const row = useMemo(() => {
    if (isBackendUnsupported) return null;
    const results = currentData?.results ?? EMPTY;
    return (
      results.find((candidate) =>
        appointmentId != null
          ? candidate.id === appointmentId
          : candidate.scribe_id === scribeId
      ) ?? null
    );
  }, [currentData, isBackendUnsupported, appointmentId, scribeId]);

  const canView =
    row !== null &&
    canViewSalesCoachReview({
      isAdminOrBusinessOwner,
      userId,
      providerId: row.provider_id,
      // The backend field where it exists; the caller's value until it ships.
      supervisingProviderId:
        row.supervising_provider_id ?? supervisingProviderId
    });

  return {
    isResolving:
      isFlagResolving ||
      isVersionResolving ||
      (!isQuerySkipped && currentData === undefined && !isError),
    canView,
    row,
    isBackendUnsupported,
    isError
  };
}
